import { useState } from "react";
/** Defaults for Income control forms, derived from records already on the page (F-120, F-123, F-125, F-128).
 * Nothing here defaults a monetary amount. */
type Period = { id: string; schedule_id?: string; starts_on?: string; ends_on?: string; status?: string };

/** Receipt posting already accepts Open and Review periods on the server. Keep
 * that choice separate from defaults for new demands and payment arrangements. */
export function receiptPeriods<T extends Period>(periods: readonly T[], scheduleId?: string): T[] {
  if (!scheduleId) return [];
  return periods.filter(p => p.schedule_id === scheduleId && (p.status === "Open" || p.status === "Review"));
}

/** Original cash dates select a receipt period only when the context is unique. */
export function receiptPeriodForDate(periods: readonly Period[], scheduleId: string | undefined, date: string): string | undefined {
  const matches = receiptPeriods(periods, scheduleId).filter(
    p => p.starts_on && p.ends_on && p.starts_on.slice(0, 10) <= date && date <= p.ends_on.slice(0, 10),
  );
  return /^\d{4}-\d{2}-\d{2}$/.test(date) && matches.length === 1 ? matches[0]!.id : undefined;
}

/** The open period whose dates contain the given YYYY-MM-DD date, if exactly one does. */
export function periodForDate(periods: readonly Period[], date: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined;
  const matches = periods.filter(
    (p) => p.status === "Open" && p.starts_on && p.ends_on && p.starts_on.slice(0, 10) <= date && date <= p.ends_on.slice(0, 10),
  );
  return matches.length === 1 ? matches[0]!.id : undefined;
}

const steps: Record<string, { days?: number; months?: number }> = {
  Weekly: { days: 7 },
  Fortnightly: { days: 14 },
  Monthly: { months: 1 },
  Quarterly: { months: 3 },
};

/** Date of the final instalment: first payment plus (instalments - 1) steps. Empty when any input is missing. */
export function arrangementCompletion(firstPayment: string, instalment: string, covered: string, frequency: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(firstPayment);
  const step = steps[frequency];
  const each = Number(instalment), total = Number(covered);
  if (!match || !step || !(each > 0) || !(total > 0)) return "";
  const count = Math.ceil(total / each - 1e-9);
  if (count > 600) return "";
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (step.days) {
    const date = new Date(Date.UTC(y, m - 1, d + step.days * (count - 1)));
    return date.toISOString().slice(0, 10);
  }
  const target = m - 1 + step.months! * (count - 1);
  const year = y + Math.floor(target / 12), month = ((target % 12) + 12) % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(d, last))).toISOString().slice(0, 10);
}

/** Id of the staff member whose display name matches, or "" when none does. */
export function staffIdByName(staff: readonly { id: string; display_name?: string }[] | undefined, name?: string): string {
  return (name && staff?.find((u) => u.display_name === name)?.id) || "";
}

/** The chosen open period: the one containing the date if any, else the person's pick, else the first open period. */
export function useDefaultPeriod(open: readonly Period[], date?: string): [string, (id: string) => void] {
  const [chosen, setChosen] = useState("");
  return [periodForDate(open, date ?? "") ?? (open.some((p) => p.id === chosen) ? chosen : (open[0]?.id ?? "")), setChosen];
}

/** F-123 state: first payment follows the start date, completion follows the instalments; either can be overtyped. */
export function useArrangementDates() {
  const [start, setStart] = useState(""), [first, setFirst] = useState<string | null>(null), [end, setEnd] = useState<string | null>(null);
  const [instalment, setInstalment] = useState(""), [covered, setCovered] = useState(""), [frequency, setFrequency] = useState("Weekly");
  const firstValue = first ?? start;
  const endValue = end ?? arrangementCompletion(firstValue, instalment, covered, frequency);
  const reset = () => { setStart(""); setFirst(null); setEnd(null); setInstalment(""); setCovered(""); setFrequency("Weekly"); };
  const restore = (values:Record<string,string[]>) => {
    setStart(values.start?.[0]??"");setFirst(values.first?.[0]??null);setEnd(values.end?.[0]??null);
    setInstalment(values.instalment?.[0]??"");setCovered(values.covered?.[0]??"");setFrequency(values.frequency?.[0]??"Weekly");
  };
  return { setStart, setFirst, setEnd, setInstalment, setCovered, setFrequency, firstValue, endValue, reset, restore };
}
